import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectDarkTheme } from '../../../../../store/slices/settingsSlice'
import {
  CargoItemInterface,
  Location,
  SlotItemInterface,
  TransportItemInterface,
} from '../../../../types/interfaces'
import {
  CARGO_ITEM_ROUTE,
  mapOptions,
  russiaCenter,
  SLOT_ITEM_ROUTE,
  TRANSPORT_ITEM_ROUTE,
} from '../../../../utils/constants'
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer'
import { Group } from '../../../../types/enums'
import { format } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { selectMapSliderVisible, toggleMapSlider } from '../../../../../store/slices/gMapSlice'

const algorithm = new SuperClusterAlgorithm({})

export const useGmapForMarkers = (
  center: Location,
  list: SlotItemInterface[] | TransportItemInterface[] | CargoItemInterface[],
) => {
  const [gMap, setGMap] = useState<google.maps.Map | undefined>(undefined)
  const dispatch = useDispatch()
  const darkTheme = useSelector(selectDarkTheme)
  const navigate = useNavigate()
  const visible = useSelector(selectMapSliderVisible)
  const [markerClusterer, setMarkerClusterer] = useState<MarkerClusterer>()
  const [markers, setMarkers] = useState<google.maps.marker.AdvancedMarkerElement[]>([])

  const initMap = useCallback(
    (id: string) => {
      const el = document.getElementById(id)
      if (el && !gMap) {
        let map = new google.maps.Map(el, {
          zoom: 3,
          maxZoom: 17,
          center: { lat: parseFloat(String(center.lat)), lng: parseFloat(String(center.lng)) },
          ...mapOptions,
          mapTypeId: google.maps.MapTypeId.ROADMAP,
          mapId: '2ae8a6eb45821208',
          colorScheme: darkTheme ? 'DARK' : 'LIGHT',
        })
        setGMap(map)
        setMarkerClusterer(new MarkerClusterer({ map, markers: [], algorithm }))
      }
    },
    [center.lat, center.lng, darkTheme, gMap],
  )

  const addInfoWindow = useCallback(
    (marker: google.maps.marker.AdvancedMarkerElement, message: string) => {
      let infoWindow = new google.maps.InfoWindow({
        content: message,
        maxWidth: 150,
      })
      google.maps.event.addListener(infoWindow, 'domready', function () {
        const attribute = marker.getAttributeNode('title')?.value
        if (attribute) {
          let clickDiv = document.getElementById(attribute)
          clickDiv &&
            clickDiv.addEventListener('click', () => {
              toOrder(attribute)
            })
        }
      })
      google.maps.event.addListener(marker, 'click', function () {
        infoWindow.open(gMap, marker)
      })
    },
    [gMap],
  )

  const updateBounds = useCallback(
    (markers: google.maps.marker.AdvancedMarkerElement[]) => {
      const currentBounds = new google.maps.LatLngBounds()

      if (markers.length === 0) {
        gMap?.fitBounds(currentBounds)
        gMap?.setCenter(russiaCenter)
        gMap?.setZoom(3)
        return
      }
      markers.forEach((marker) => {
        if (marker.position) currentBounds.extend(marker.position)
      })
      gMap?.fitBounds(currentBounds)
    },
    [gMap],
  )

  const resetBounds = () => {
    updateBounds(markers)
  }

  const setMarkersAction = useCallback(
    (
      list: SlotItemInterface[] | TransportItemInterface[] | CargoItemInterface[],
      gMap: google.maps.Map,
    ) => {
      const deleted = markers.filter(
        (marker) =>
          //@ts-ignore
          !list.map((el) => !`${el.group}_${el.id}`).includes(marker.getAttribute('title')),
      )

      const newMarkers: google.maps.marker.AdvancedMarkerElement[] = []

      const added = list.filter(
        (element) =>
          !markers.map((el) => el.getAttribute('title')).includes(`${element.group}+${element.id}`),
      )

      for (const element of added) {
        const pin = new google.maps.marker.PinElement({
          background:
            element.group === Group.CARGO
              ? 'rgba(40, 102, 110, 0.8)'
              : element.group === Group.TRANSPORT
              ? 'rgba(124, 152, 133, 0.8)'
              : 'rgba(181, 182, 130, 0.8)',
          borderColor:
            element.group === Group.CARGO
              ? 'rgba(40, 102, 110, 0.8)'
              : element.group === Group.TRANSPORT
              ? 'rgba(124, 152, 133, 0.8)'
              : 'rgba(181, 182, 130, 0.8)',
          glyphColor: 'white',
        })
        if (element.points) {
          let marker = new google.maps.marker.AdvancedMarkerElement({
            position: {
              lat: Number(
                element.points[0].address
                  ? element.points[0].address.latitude
                  : element.points[0].city.latitude,
              ),
              lng: Number(
                element.points[0].address
                  ? element.points[0].address.longitude
                  : element.points[0].city.longitude,
              ),
            },
            // map: gMap,
            title: `${element.group}_${element.id}`,
            content: pin.element,
          })

          let message = `
                  <div style="font-size:10px; font-weight:normal">
        
                   ${element.name}</div>
                  <div style="font-size:10px; font-weight:normal">Ставка ${
                    !element.cost ? 'не указана' : element.cost
                  }</div>
                ${
                  element.group !== Group.TRANSPORT
                    ? `<div style="font-size:10px; font-weight:normal">Время подачи</div>`
                    : ''
                }
                   ${
                     element.group !== Group.TRANSPORT
                       ? `<div style="font-size:10px; font-weight:normal">${format(
                           new Date(element.startTime),
                           'dd.MM.yyy HH:mm',
                         )}</div>`
                       : ''
                   }
                  ${
                    element.group !== Group.TRANSPORT
                      ? ` <div style="font-size:10px; font-weight:normal">Подача</div>`
                      : ''
                  }
                  <div style="font-size:10px; font-weight:normal">${
                    element.points[0].address
                      ? element.points[0].address.value + ', ' + element.points[0].city.value
                      : element.points[0].city.value
                  }</div>
                  ${
                    element.points.length > 1
                      ? `<div style="font-size:10px; font-weight:normal">Завершение</div>
                  <div style="font-size:10px; font-weight:normal">${
                    element.points[element.points.length - 1].address
                      ? element.points[element.points.length - 1].address.value +
                        ', ' +
                        element.points[element.points.length - 1].city.value
                      : element.points[element.points.length - 1].city.value
                  }</div>`
                      : ''
                  }

                  ${
                    element.group !== Group.SLOT && element.group !== Group.TRANSPORT
                      ? `<div style="font-size:10px; font-weight:normal">Всего точек ${element.points.length}</div>`
                      : ''
                  }

                  <button id='${element.group}_${element.id}'
                  style="font-size:8px; font-weight:normal;background-color:green; color: white; border: none; padding: 5px; border-radius: 5px; cursor: pointer; margin-top: 5px;"
                  >ОТКРЫТЬ</button>
                  `

          addInfoWindow(marker, message)
          newMarkers.push(marker)
        }
      }

      markerClusterer?.clearMarkers()

      // markerClusterer?.removeMarkers(deleted)
      // markerClusterer?.addMarkers(newMarkers)

      setMarkers((prev) => {
        markerClusterer?.addMarkers([
          ...prev.filter(
            (el) =>
              !deleted.map((el) => el.getAttribute('title')).includes(el.getAttribute('title')),
          ),
          ...newMarkers,
        ])
        updateBounds([
          ...prev.filter(
            (el) =>
              !deleted.map((el) => el.getAttribute('title')).includes(el.getAttribute('title')),
          ),
          ...newMarkers,
        ])
        return [
          ...prev.filter(
            (el) =>
              !deleted.map((el) => el.getAttribute('title')).includes(el.getAttribute('title')),
          ),
          ...newMarkers,
        ]
      })
    },

    [addInfoWindow, markers, markerClusterer, updateBounds],
  )

  const toOrder = (title: string) => {
    const group = title.split('_')[0]
    const id = title.split('_')[1]
    switch (group) {
      case Group.SLOT:
        navigate(`${SLOT_ITEM_ROUTE}/${id}`)
        break
      case Group.TRANSPORT:
        navigate(`${TRANSPORT_ITEM_ROUTE}/${id}`)
        break
      case Group.CARGO:
        navigate(`${CARGO_ITEM_ROUTE}/${id}`)
        break
      default:
        break
    }
  }

  const toggleMap = () => {
    dispatch(toggleMapSlider())
  }

  useEffect(() => {
    if (visible) initMap('map')
  }, [visible])

  useEffect(() => {
    if (gMap && visible) {
      setMarkersAction(list, gMap)
    }
  }, [list, visible, gMap])

  return { visible, toggleMap, resetBounds }
}

